// Companion/blocks/ChatActionCardBody.tsx
//
// What a ChatActionCard says about its action: the header and the detail rows
// that fold away once it runs. The generic layout serves every action; an
// outside score report has its own (ORA-1184). The consent mechanics — confirm,
// Skip, failure, undo — stay in ChatActionCard and are shared by both.
import { Fragment } from 'react';
import {
  ChatCircleDots,
  ListChecks,
  Sparkle,
  type Icon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import type { ChatActionProposal } from '@/slices/chat/chatSlice';
import { OutsideResultDetails, OutsideResultHeader } from './OutsideResultCard';
import {
  readOutsideResultCard,
  type OutsideResultCard,
} from './outsideResultCardData';

/**
 * A human label for an action name, so the card never shows a tool identifier.
 * V1 ships one action; an unknown name falls back to the summary alone, which
 * is the sentence the registry made the backend write.
 */
const ACTION_LABELS: Record<string, string> = {
  create_qbank_block: 'Create a QBank block',
  contact_support: 'Message Ora support',
};

/** The tile icon that says, at a glance, what kind of action this is. */
const ACTION_ICONS: Record<string, Icon> = {
  create_qbank_block: ListChecks,
  contact_support: ChatCircleDots,
};

/**
 * The confirm button says what confirming does. The server may override it per
 * proposal (`confirmLabel`); this map is the client's own copy for the actions
 * it knows, and "Continue" is the neutral fallback.
 */
const CONFIRM_LABELS: Record<string, string> = {
  contact_support: 'Send to Support',
};

/** Args are shown so consent is informed. These are the ones V1 can produce. */
const ARG_LABELS: Record<string, string> = {
  examId: 'Exam',
  topicIds: 'Topics',
  count: 'Questions',
  mode: 'Mode',
  topic: 'About',
};

/**
 * Args the card's own summary already says out loud. `contact_support`'s
 * summary is the message itself — printing it a second time in the arg list
 * would show the student the same paragraph twice.
 */
const REDUNDANT_ARGS: Record<string, readonly string[]> = {
  contact_support: ['summary'],
};

const formatArgValue = (value: unknown): string => {
  if (Array.isArray(value)) return `${value.length}`;
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return '—';

  return String(value);
};

/**
 * What the card lists under its title. The backend may render the rows
 * itself (`details`, ORA-607); until it does, the args are humanised here.
 */
const actionDetailRows = (
  proposal: ChatActionProposal
): { label: string; value: string }[] => {
  const hiddenArgs = REDUNDANT_ARGS[proposal.name] ?? [];

  return proposal.details && proposal.details.length > 0
    ? proposal.details
    : Object.entries(proposal.args)
        .filter(([key]) => !hiddenArgs.includes(key))
        .map(([key, value]) => ({
          label: ARG_LABELS[key] ?? key,
          value: formatArgValue(value),
        }));
};

/**
 * What one proposal's card shows. An outside score report gets its own header
 * and body (ORA-1184); a card proposed before those fields existed keeps the
 * generic rows. Everything else on the card — confirm, Skip, failure, undo —
 * stays shared.
 */
export const describeActionCard = (proposal: ChatActionProposal) => {
  const outsideResult = readOutsideResultCard(proposal.name, proposal.args);

  return {
    outsideResult,
    detailRows: actionDetailRows(proposal),
    confirmLabel:
      outsideResult?.confirmLabel ??
      proposal.confirmLabel ??
      CONFIRM_LABELS[proposal.name] ??
      'Continue',
  };
};

export interface ChatActionCardHeaderProps {
  proposal: ChatActionProposal;
  outsideResult: OutsideResultCard | null;
  isSettled: boolean;
}

export const ChatActionCardHeader = ({
  proposal,
  outsideResult,
  isSettled,
}: ChatActionCardHeaderProps) => {
  if (outsideResult) {
    return <OutsideResultHeader card={outsideResult} isSettled={isSettled} />;
  }

  const KindIcon = ACTION_ICONS[proposal.name] ?? Sparkle;

  return (
    <div className="flex items-start gap-3">
      <span
        aria-hidden="true"
        className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-surface-muted text-fg-secondary"
      >
        <KindIcon size={18} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-fg-secondary">
            {ACTION_LABELS[proposal.name] ?? 'Ora suggests'}
          </span>
          {!isSettled ? (
            <span className="rounded-full bg-surface-muted px-2 py-0.5 text-xs text-fg-secondary">
              Needs your OK
            </span>
          ) : null}
        </div>
        <strong className="mt-0.5 block font-heading text-base font-medium text-fg-primary">
          {proposal.summary}
        </strong>
      </div>
    </div>
  );
};

export interface ChatActionCardDetailsProps {
  proposal: ChatActionProposal;
  outsideResult: OutsideResultCard | null;
  detailRows: { label: string; value: string }[];
  /** False once the card settled: the details fold out of sight. */
  isOpen: boolean;
}

/**
 * The details fold away when the action runs, so the card changes height
 * smoothly instead of snapping the thread up.
 */
export const ChatActionCardDetails = ({
  proposal,
  outsideResult,
  detailRows,
  isOpen,
}: ChatActionCardDetailsProps) => (
  <div
    aria-hidden={isOpen ? undefined : true}
    className={cn(
      'grid transition-all duration-300 ease-chat-out',
      isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
    )}
  >
    <div className="overflow-hidden">
      {outsideResult ? (
        <OutsideResultDetails
          card={outsideResult}
          repeatNote={outsideResult.isRepeat ? proposal.summary : null}
          isFolded={!isOpen}
        />
      ) : (
        <dl className="m-0 mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          {detailRows.map((row) => (
            <Fragment key={row.label}>
              <dt className="text-fg-secondary">{row.label}</dt>
              <dd className="m-0 min-w-0 break-words text-fg-primary">
                {row.value}
              </dd>
            </Fragment>
          ))}
        </dl>
      )}
    </div>
  </div>
);
